// §4.14 THE DEAL LIST, /sales/opportunities (Module 09, P3-E2 PR E2-1;
// D-312, D-314). The quote book's shape (screens/quotes.tsx), which carries
// the full argument for each part; this header names what differs.
//
// SAVED VIEWS AS `sales.opportunities` (spec 16 §3.12). The filters live in
// the address under the list's keys (`screens/pipeline-search-params.ts`),
// every write goes through the controller, and the list read waits for the
// views read on `?view=` alone, so the region reveals once with the applied
// view (design-system §5). Rotting and Needs a next step are system views.
//
// VALUE AND WEIGHTED FOLLOW THE PAYLOAD (§7, D-312 ruling 12). Both columns
// declare `presence`: they exist on a page only when a row carries the key, so
// a reader who is not sent values sees neither column, neither caret and no
// chooser entry. `null` is a different fact from absence: nobody estimated
// the value, which reads "No value" and never zero (D-312 ruling 7).
//
// D-301'S MONEY SORT, for `expected_value_minor`. The validator drops
// `order=±expected_value_minor` for a reader the mirror knows is refused, the
// screen takes the dropped token out of the address, and a server 403 for a
// reader the mirror cannot judge drops the order through the controller and
// asks again. Both arms keep `view` and every filter (D-305 ruling 7).
//
// THE SEARCH (`q`) is typed text, debounced into the address, as on the
// customer book. It matches a customer's name only on deals whose customer
// the reader may open (§7's two-doors paragraph), which is the server's
// business; the box says only what it searches.
//
// EXPORT CSV (§4.14, §4.1's rules, D-248): `sales-opportunities-export`
// walks this list's read with its filters and effective order, 200 rows a
// page up to 10 000, and writes the hand-listed deal columns
// (`lib/exports.ts`), the two money columns only when a row carries them.
//
// ONE REVEAL (§4.14 States, design-system §5). The filter row sits inside the
// views region, and the region waits for the stages read and, for an admin,
// the member directory as well as the views and the first page: the Stage
// select and the Stage column are decided by the stage names, and painting
// either before them shifted the row sideways and printed pack codes.
//
// THE PEEK (§4.15). Each row's Peek button opens the deal beside the list;
// a row click still opens the deal's page. `peek` is outside every view.
//
// THE STAGE CELL (P3-E2 PR E2-2d, §4.14's E2-2d annotation, D-326 rulings 3
// and 4). The Stage column edits in place (design-system §3.13): it lists the
// stages, then Won and Lost, and moves the deal through `/move` naming the
// stage the cell showed as `from_stage_id`. Won and Lost send nothing from the
// cell. They open the deal page's own dialogs, because a win asks what won the
// deal and a loss asks why (D-312). A refused move that names the deal's stage
// (`OPPORTUNITY_STAGE_CHANGED`, `current_stage_id`) shows that stage and says
// it, and the move hook re-reads every deal read whatever the answer.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesFilterBar } from "../components/sales-filter-bar";
import { Button } from "../../../../components/button";
import type { CellOption, SelectCellEditor } from "../../../../components/cell-editor";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { peekCode } from "../../../../components/peek-address";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { formatTimestamp } from "../../../../lib/datetime";
import { bookPager, useBookPages, useBookWindow } from "../hooks/book-pages";
import { UnauthorizedError } from "../../../../lib/problem";
import { newIdempotencyKey, RequestError, requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useSavedViews, ViewSwitcher, ViewsRegion } from "../../views";
import { columnPresent, formatMinor } from "../lib/money";
import {
  addressCarriesRefusedValueOrder,
  isValueOrderRefusal,
  valueSortVerdict,
} from "../lib/value-order";
import {
  currentStageIdOf,
  dealStatusLabel,
  definedOnly,
  formatMonth,
  isLostReasonCode,
  LOST_REASON_CODES,
  lostReasonLabel,
  refusalText,
  stageName,
} from "../lib/pipeline";
import { opportunitiesCsv, opportunitiesFilters } from "../lib/exports";
import {
  SalesExportButton,
  SalesExportNotes,
  SalesExportSlot,
  useSalesExport,
} from "../components/export-csv";
import {
  canWriteOpportunities,
  isSalesReadRefused,
  useSalesPermissions,
} from "../hooks/permissions";
import { useCustomer360, useOwnerIndex } from "../hooks/sales-queries";
import {
  fetchOpportunitiesPage,
  useLoseOpportunity,
  useMoveOpportunity,
  useOpportunities,
  usePipelineStages,
  useWinOpportunity,
  type OpportunitiesQuery,
  type Opportunity,
} from "../hooks/opportunity-queries";
import {
  DealStatusChip,
  IdleChip,
  NextStepCell,
  PipelineCustomerFilter,
  PipelineOwnerFilter,
  PipelineTabs,
  StageText,
} from "../components/pipeline/pipeline-parts";
import { LoseDealModal, NewDealModal, WinDealModal } from "../components/pipeline/deal-dialogs";
import { SalesPeek, useSalesPeek } from "../components/peek/sales-peek";
import { useReadsHeldOffline } from "../lib/reads";
import { compactSearch, SALES_SEARCH_TEXT_MAX } from "./search-params";
import {
  isDealValueOrderToken,
  isOpportunitiesOrderToken,
  OPPORTUNITIES_DEFAULT_ORDER,
  OPPORTUNITY_CLOSED_STATUSES,
  validateSalesOpportunitiesSearch,
  type OpportunitiesOrderToken,
  type SalesOpportunitiesSearch,
} from "./pipeline-search-params";

/** The list's ascending base keys, the type a column's `sortKey` carries. */
type OpportunitiesOrderKey = OpportunitiesOrderToken extends `-${infer Base}`
  ? Base
  : OpportunitiesOrderToken;

/** The Stage cell's two closing choices, after the stages. Neither is a stage
 * id (a stage id is a uuid), and neither is sent: each opens its dialog. */
const WON_CHOICE = "won";
const LOST_CHOICE = "lost";

/** The words the list's filter bar names each view filter by (V5): a saved
 * view's refusal that names a stored filter reads these. */
export function opportunityFilterLabels(t: (key: string) => string): Record<string, string> {
  return {
    status: t("sales.pipeline.list.filterStatus"),
    stage: t("sales.pipeline.list.filterStage"),
    owner: t("sales.pipeline.filterOwner"),
    customer: t("sales.pipeline.filterCustomer"),
    close_from: t("sales.pipeline.list.filterCloseFrom"),
    close_to: t("sales.pipeline.list.filterCloseTo"),
    rotting: t("sales.pipeline.list.filterRotting"),
    needs_next_step: t("sales.pipeline.list.filterNeedsNextStep"),
    lost_reason: t("sales.pipeline.list.filterLostReason"),
    q: t("sales.pipeline.list.search"),
  };
}

/** The wire query the address asks for (pure). */
export function opportunitiesQueryOf(search: SalesOpportunitiesSearch): OpportunitiesQuery {
  return definedOnly({
    status: search.status,
    stage_id: search.stage,
    owner_user_id: search.owner,
    customer_id: search.customer,
    close_from: search.close_from,
    close_to: search.close_to,
    rotting: search.rotting === true ? ("true" as const) : undefined,
    needs_next_step: search.needs_next_step === true ? ("true" as const) : undefined,
    lost_reason: search.lost_reason,
    q: search.q,
    order: search.order,
  });
}

export function SalesOpportunitiesScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const peek = useSalesPeek();

  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const valueSort = valueSortVerdict(perms);
  const validate = useMemo(
    () => (raw: Record<string, unknown>) => validateSalesOpportunitiesSearch(raw, valueSort),
    [valueSort],
  );
  const search = useMemo(() => validate(rawSearch), [rawSearch, validate]);
  const setSearch = (next: SalesOpportunitiesSearch) => {
    void navigate({ to: "/sales/opportunities", search: compactSearch(next), replace: true });
  };
  const order = search.order ?? OPPORTUNITIES_DEFAULT_ORDER;

  const refused = isSalesReadRefused(perms, perms.canOpenPipeline);
  const canWrite = canWriteOpportunities(perms);
  const owners = useOwnerIndex(perms.isAdmin);
  const offline = useReadsHeldOffline();
  const stages = usePipelineStages(!refused);
  const stageList = useMemo(() => stages.data?.items ?? [], [stages.data]);
  const nameOfStage = (id: string) => {
    const stage = stageList.find((s) => s.id === id);
    return stage === undefined ? undefined : stageName(stage, i18n.language);
  };

  const [creating, setCreating] = useState(false);
  // The Stage cell's Won or Lost, open in the deal page's dialog.
  const [closing, setClosing] = useState<{
    readonly deal: Opportunity;
    readonly outcome: typeof WON_CHOICE | typeof LOST_CHOICE;
  } | null>(null);
  const move = useMoveOpportunity();
  const win = useWinOpportunity();
  const lose = useLoseOpportunity();

  const query = useMemo(() => opportunitiesQueryOf(search), [search]);
  const queryKey = JSON.stringify(query);
  // One page at a time (D-560): the stack starts again at page 1 whenever
  // the query does.
  const pages = useBookPages(queryKey);
  const cursor = pages.cursor;

  const isFiltered =
    search.status !== undefined ||
    search.stage !== undefined ||
    search.owner !== undefined ||
    search.customer !== undefined ||
    search.close_from !== undefined ||
    search.close_to !== undefined ||
    search.rotting !== undefined ||
    search.needs_next_step !== undefined ||
    search.lost_reason !== undefined ||
    search.q !== undefined;

  // D-301: an address carrying a money order waits for the role feed.
  const holdForFeed =
    search.order !== undefined && isDealValueOrderToken(search.order) && valueSort === "pending";

  const [listGate, setListGate] = useState(false);
  const shown = useOpportunities(query, cursor, false);
  const shownData = listGate ? shown.data : undefined;
  const rows = useMemo(() => shownData?.items ?? [], [shownData]);
  const turning = listGate && shown.isPlaceholderData;
  const pageView = useBookWindow(pages, {
    items: rows,
    nextCursor: shownData?.next_cursor,
    turning,
    settled: shownData !== undefined && !shown.isFetching,
  });

  const stageOptions: CellOption[] = [
    ...stageList.map((stage) => ({ value: stage.id, label: stageName(stage, i18n.language) })),
    { value: WON_CHOICE, label: t("sales.pipeline.deal.markWon") },
    { value: LOST_CHOICE, label: t("sales.pipeline.deal.markLost") },
  ];
  const stageEditor: SelectCellEditor<Opportunity> = {
    kind: "select",
    testId: "sales-deal-stage-cell",
    // The deal's own `mutable` says whether this reader's write doors answer
    // for it (narrowing 16), and only an open deal moves.
    writable: (row) => canWrite && row.mutable && row.status === "open",
    // A closed deal says so to every reader, since no role moves it here.
    reason: (row) =>
      row.status !== "open"
        ? t("sales.cells.stage.closed")
        : !canWrite || !row.mutable
          ? t("sales.cells.stage.notAllowed")
          : undefined,
    editLabel: (row) => t("sales.cells.stage.edit", { title: row.title }),
    listLabel: (row) => t("sales.cells.stage.list", { title: row.title }),
    busyLabel: t("sales.common.saving"),
    failedLabel: t("sales.cells.stage.failed"),
    value: (row) => row.stage_id,
    options: () => stageOptions,
    display: (value, row) => {
      const id = value ?? row.stage_id;
      const stage = stageList.find((s) => s.id === id);
      return (
        <StageText
          name={
            stage !== undefined
              ? stageName(stage, i18n.language)
              : id === row.stage_id
                ? row.stage_code
                : t("sales.common.notListed")
          }
          pct={stage?.probability_pct ?? row.probability_pct}
        />
      );
    },
    divert: (row, next) => {
      if (next !== WON_CHOICE && next !== LOST_CHOICE) return false;
      win.reset();
      lose.reset();
      setClosing({ deal: row, outcome: next });
      return true;
    },
    send: async (row, next, shown) => {
      try {
        await move.mutateAsync({
          id: row.id,
          body: { stage_id: next, from_stage_id: shown ?? row.stage_id },
          idempotencyKey: newIdempotencyKey(),
        });
        return { status: "committed" };
      } catch (error) {
        // A 401 ends the session, which is no refusal of the move: the cell
        // puts the stage back and the reader signs in again (§3.13).
        if (error instanceof UnauthorizedError) {
          void navigate({ to: "/login" });
          return {
            status: "refused",
            code: "UNAUTHENTICATED",
            message: t("sales.cells.signedOut"),
          };
        }
        // A request that got no answer is the cell's own failure.
        if (!(error instanceof RequestError)) throw error;
        const code = error.code ?? String(error.httpStatus);
        const message = refusalText(t, error, t("sales.pipeline.move.failed"), nameOfStage);
        const current = code === "OPPORTUNITY_STAGE_CHANGED" ? currentStageIdOf(error) : undefined;
        return current !== undefined
          ? { status: "changed", current, code, message }
          : { status: "refused", code, message };
      }
    },
    // `useMoveOpportunity` re-reads every deal read when the move settles,
    // refused or not, so the cell has nothing more to invalidate.
    onSettled: () => undefined,
  };
  const closeDialog = () => {
    setClosing(null);
  };

  const columns: DataTableColumn<Opportunity, OpportunitiesOrderKey>[] = [
    {
      key: "title",
      header: t("sales.pipeline.list.colDeal"),
      id: true,
      render: (row) => (
        <span className="sales-deal-cell">
          <span data-testid="sales-deal-title">{row.title}</span>
          <span className="sales-hint num">{row.opportunity_no}</span>
        </span>
      ),
    },
    {
      key: "customer",
      phonePriority: 1,
      header: t("sales.pipeline.list.colCustomer"),
      render: (row) => <RefLabel value={row.customer_name ?? undefined} id={row.customer_id} />,
    },
    {
      key: "stage",
      phonePriority: 2,
      header: t("sales.pipeline.list.colStage"),
      editable: stageEditor,
    },
    {
      key: "expected_value_minor",
      kind: "money",
      phonePriority: 3,
      header: t("sales.pipeline.list.colValue"),
      numeric: true,
      sortKey: "expected_value_minor",
      sortFirst: "desc",
      presence: (loaded) => columnPresent(loaded, (row) => row.expected_value_minor),
      render: (row) =>
        row.expected_value_minor === undefined ? null : row.expected_value_minor === null ? (
          <span className="sales-hint" data-testid="sales-deal-value" data-valued="false">
            {t("sales.pipeline.noValue")}
          </span>
        ) : (
          <span className="num" data-testid="sales-deal-value" data-valued="true">
            {formatMinor(row.expected_value_minor, row.currency, i18n.language)}
          </span>
        ),
    },
    {
      key: "weighted_value_minor",
      kind: "money",
      header: t("sales.pipeline.list.colWeighted"),
      numeric: true,
      presence: (loaded) => columnPresent(loaded, (row) => row.weighted_value_minor),
      render: (row) =>
        row.weighted_value_minor === undefined || row.weighted_value_minor === null ? null : (
          <span
            className="num"
            data-testid="sales-deal-weighted"
            data-weighted={row.weighted_value_minor}
          >
            {formatMinor(row.weighted_value_minor, row.currency, i18n.language)}
          </span>
        ),
    },
    {
      key: "expected_close_month",
      kind: "date",
      header: t("sales.pipeline.list.colCloseMonth"),
      sortKey: "expected_close_month",
      render: (row) => (
        <span
          className={row.close_month_passed ? "sales-valid-urgent" : undefined}
          data-testid="sales-deal-close"
          data-month={row.expected_close_month}
          data-passed={row.close_month_passed ? "true" : "false"}
        >
          {formatMonth(row.expected_close_month, i18n.language)}
          {row.close_month_passed ? (
            <span className="visually-hidden"> {t("sales.pipeline.closeMonthPassed")}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "next_step",
      kind: "status",
      header: t("sales.pipeline.list.colNextStep"),
      render: (row) => (
        <NextStepCell next={row.next_activity} needsNextStep={row.needs_next_step} />
      ),
    },
    {
      key: "stage_entered_at",
      kind: "date",
      header: t("sales.pipeline.list.colInStage"),
      sortKey: "stage_entered_at",
      render: (row) => <IdleChip days={row.idle_days} rotting={row.rotting} />,
    },
    {
      key: "owner",
      header: t("sales.pipeline.list.colOwner"),
      render: (row) => row.owner_name ?? t("sales.pipeline.ownerNone"),
    },
    {
      key: "status",
      kind: "status",
      header: t("sales.pipeline.list.colStatus"),
      render: (row) => (
        <span>
          <DealStatusChip status={row.status} />
          {row.status === "lost" && isLostReasonCode(row.lost_reason_code) ? (
            <span className="sales-provisional" data-testid="sales-deal-lost-reason">
              {t("sales.pipeline.lostBecause", {
                reason: lostReasonLabel(t, row.lost_reason_code),
              })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "updated_at",
      kind: "date",
      header: t("sales.pipeline.list.colUpdated"),
      defaultHidden: true,
      render: (row) => formatTimestamp(row.updated_at, i18n.language),
    },
  ];

  const views = useSavedViews<SalesOpportunitiesSearch, Opportunity, OpportunitiesOrderKey>({
    screenKey: "sales.opportunities",
    search,
    setSearch,
    validate,
    columns,
    rows,
    rowsLoading: !listGate || shown.isFetching,
    filterLabels: opportunityFilterLabels(t),
  });
  if (listGate !== views.listEnabled) setListGate(views.listEnabled);

  const live = useOpportunities(query, cursor, !refused && !holdForFeed && listGate);
  const page = {
    data: listGate ? live.data : undefined,
    error: listGate ? live.error : null,
    isPending: !listGate || live.isPending,
    isFetching: listGate && live.isFetching,
    fetchStatus: listGate ? live.fetchStatus : "idle",
    refetch: live.refetch,
  };

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  // D-301's two arms, handed to the controller (the quote book's shape).
  const valueOrderRefused =
    search.order !== undefined &&
    isDealValueOrderToken(search.order) &&
    isValueOrderRefusal(page.error);
  const valueOrderDropped = addressCarriesRefusedValueOrder(rawSearch, valueSort);
  const dropRef = useRef(views.dropRefusedOrder);
  dropRef.current = views.dropRefusedOrder;
  const refusedToken = search.order;
  const droppedToken = typeof rawSearch["order"] === "string" ? rawSearch["order"] : undefined;
  useEffect(() => {
    if (valueOrderRefused) dropRef.current(refusedToken);
    else if (valueOrderDropped) dropRef.current(droppedToken);
  }, [valueOrderRefused, valueOrderDropped]);

  // The search box: typed text, debounced into the address (customers.tsx).
  const [qText, setQText] = useState(search.q ?? "");
  const debouncedQ = useDebouncedValue(qText.trim(), 250);
  const qWritten = useRef(search.q);
  useEffect(() => {
    if (search.q === qWritten.current) return;
    qWritten.current = search.q;
    setQText(search.q ?? "");
  }, [search.q]);
  const writeRef = useRef(views.write);
  writeRef.current = views.write;
  const searchRef = useRef(search);
  searchRef.current = search;
  useEffect(() => {
    const now = searchRef.current;
    if (debouncedQ === (now.q ?? "").trim()) return;
    const next = debouncedQ.length > 0 ? debouncedQ : undefined;
    qWritten.current = next;
    writeRef.current({ ...now, q: next });
  }, [debouncedQ]);

  const setOrder = (next: string) => {
    if (!isOpportunitiesOrderToken(next)) return;
    views.write({ ...search, order: next === OPPORTUNITIES_DEFAULT_ORDER ? undefined : next });
  };

  const filteredCustomer = useCustomer360(search.customer ?? "");
  const customerName =
    filteredCustomer.data?.customer.display_name ??
    rows.find((row) => row.customer_id === search.customer)?.customer_name ??
    undefined;
  const exporter = useSalesExport<Opportunity>({
    list: "opportunities",
    resetKey: queryKey,
    fetchPage: (next, limit, signal) =>
      fetchOpportunitiesPage({ ...query, order, limit }, next, signal),
    serialize: opportunitiesCsv,
    filters: opportunitiesFilters(t, {
      q: search.q ?? "",
      status: search.status === undefined ? undefined : dealStatusLabel(t, search.status),
      stage: search.stage === undefined ? undefined : (nameOfStage(search.stage) ?? search.stage),
      owner:
        search.owner !== undefined ? (owners.byId.get(search.owner) ?? search.owner) : undefined,
      customer: search.customer === undefined ? undefined : (customerName ?? search.customer),
      closeFrom: search.close_from ?? "",
      closeTo: search.close_to ?? "",
      rotting: search.rotting === true,
      needsNextStep: search.needs_next_step === true,
      lostReason:
        search.lost_reason === undefined ? undefined : lostReasonLabel(t, search.lost_reason),
    }),
    order,
    namesPending: search.customer !== undefined && customerName === undefined,
  });

  const paused = page.fetchStatus === "paused";
  // The server's refusal of the list for a reader the mirror could not judge
  // (a tenant's composed role) is the mirror's refusal said late, so it reads
  // the same sentence with no Retry, as the forecast's does. A 403 naming
  // `sales.read_value` is the money order's, which the controller drops and
  // asks again (D-301), never a refusal of the list.
  const listRefused =
    page.error instanceof RequestError &&
    page.error.httpStatus === 403 &&
    !isValueOrderRefusal(page.error);
  const listSettled = page.data !== undefined || page.error !== null;
  // The stage names and, for an admin, the owner names decide the filter row's
  // widths and the Stage column's words, so the region waits for them too. A
  // failed read still opens it (fail-closed absence): the Stage column then
  // prints the stage's code, the one thing left to print.
  const stagesSettled =
    stages.data !== undefined || stages.error !== null || stages.fetchStatus === "paused";
  // A directory read held offline reports pending until the network returns,
  // and must not hold the region for the whole cut (design-system §3.9): the
  // region then opens with the offline sentence and owner ids in place of
  // names.
  const ownersSettled = !perms.isAdmin || !owners.isPending || offline;
  const ready =
    refused || (views.settled && (listSettled || paused) && stagesSettled && ownersSettled);

  const chipLabel = (filter: string, value: string): string =>
    t("sales.common.filterChip", { filter, value });

  return (
    <section className="sales-opportunities-screen" data-testid="sales-opportunities-screen">
      <PageHeader
        title={t("sales.pipeline.title")}
        actions={
          <>
            <div className="sales-toolbar">
              {refused ? null : rows.length > 0 ? (
                <SalesExportButton exporter={exporter} />
              ) : (
                <SalesExportSlot />
              )}
              {canWrite && !refused ? (
                <Button
                  variant="primary"
                  data-testid="sales-pipeline-new-deal"
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  {t("sales.pipeline.newDeal")}
                </Button>
              ) : null}
            </div>
          </>
        }
      />

      <PipelineTabs active="list" shared={{ owner: search.owner, customer: search.customer }} />

      <SalesExportNotes exporter={exporter} />

      <ViewsRegion ready={ready} cols={columns.length}>
        {/* The filter row is part of the reveal: its Stage select is decided
            by the stages read, and painted before it the select widens and
            pushes every control after it (design-system §5). */}
        {refused ? null : (
          <>
            <SalesFilterBar
              data-testid="sales-deals-filters"
              search={{
                value: qText,
                label: t("sales.pipeline.list.search"),
                placeholder: t("sales.pipeline.list.searchHint"),
                "data-testid": "sales-deals-q",
                onChange: (value) => {
                  setQText(value.includes("\u0000") ? "" : value.slice(0, SALES_SEARCH_TEXT_MAX));
                },
              }}
              onClearAll={() => {
                views.write({
                  ...search,
                  owner: undefined,
                  customer: undefined,
                  status: undefined,
                  stage: undefined,
                  close_from: undefined,
                  close_to: undefined,
                  lost_reason: undefined,
                  rotting: undefined,
                  needs_next_step: undefined,
                });
              }}
              filters={[
                {
                  // The owner and customer the board and the forecast share,
                  // behind Filters here so the row keeps status alone (at most
                  // two inline). Each is a chip once chosen, at every width.
                  key: "owner",
                  applied:
                    search.owner === undefined
                      ? undefined
                      : chipLabel(
                          t("sales.pipeline.filterOwner"),
                          owners.byId.get(search.owner) ??
                            (owners.isPending && !offline
                              ? t("ref.resolving")
                              : t("sales.common.notListed")),
                        ),
                  onClear: () => {
                    views.write({ ...search, owner: undefined });
                  },
                  control: (
                    <PipelineOwnerFilter
                      owner={search.owner}
                      onOwner={(owner) => {
                        views.write({ ...search, owner });
                      }}
                      showOwner={perms.isAdmin}
                      owners={owners.byId}
                      ownersPending={owners.isPending && !offline}
                      testPrefix="sales-deals"
                    />
                  ),
                },
                {
                  key: "customer",
                  applied:
                    search.customer === undefined
                      ? undefined
                      : chipLabel(
                          t("sales.pipeline.filterCustomer"),
                          customerName ??
                            (filteredCustomer.isPending
                              ? t("ref.resolving")
                              : t("sales.common.notListed")),
                        ),
                  onClear: () => {
                    views.write({ ...search, customer: undefined });
                  },
                  control: (
                    <PipelineCustomerFilter
                      customer={search.customer}
                      onCustomer={(customer) => {
                        views.write({ ...search, customer });
                      }}
                      testPrefix="sales-deals"
                      chip={false}
                    />
                  ),
                },
                {
                  key: "status",
                  inline: true,
                  applied:
                    search.status === undefined
                      ? undefined
                      : chipLabel(
                          t("sales.pipeline.list.filterStatus"),
                          dealStatusLabel(t, search.status),
                        ),
                  onClear: () => {
                    views.write({ ...search, status: undefined });
                  },
                  control: (
                    <Field label={t("sales.pipeline.list.filterStatus")}>
                      <Select
                        value={search.status ?? ""}
                        data-testid="sales-deals-status"
                        onChange={(event) => {
                          views.write({
                            ...search,
                            status: OPPORTUNITY_CLOSED_STATUSES.find(
                              (s) => s === event.target.value,
                            ),
                          });
                        }}
                      >
                        <option value="">{t("sales.pipeline.status.open")}</option>
                        {OPPORTUNITY_CLOSED_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {dealStatusLabel(t, status)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ),
                },
                {
                  key: "stage",
                  applied:
                    search.stage === undefined
                      ? undefined
                      : chipLabel(
                          t("sales.pipeline.list.filterStage"),
                          (() => {
                            const stage = stageList.find((s) => s.id === search.stage);
                            return stage === undefined
                              ? t("sales.common.notListed")
                              : stageName(stage, i18n.language);
                          })(),
                        ),
                  onClear: () => {
                    views.write({ ...search, stage: undefined });
                  },
                  control: (
                    <Field label={t("sales.pipeline.list.filterStage")}>
                      <Select
                        value={search.stage ?? ""}
                        data-testid="sales-deals-stage"
                        onChange={(event) => {
                          views.write({
                            ...search,
                            stage: event.target.value.length > 0 ? event.target.value : undefined,
                          });
                        }}
                      >
                        <option value="">{t("sales.pipeline.list.anyStage")}</option>
                        {search.stage !== undefined &&
                        !stageList.some((s) => s.id === search.stage) ? (
                          <option value={search.stage}>
                            {stages.isPending ? t("ref.resolving") : t("sales.common.notListed")}
                          </option>
                        ) : null}
                        {stageList.map((stage) => (
                          <option key={stage.id} value={stage.id}>
                            {stageName(stage, i18n.language)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ),
                },
                {
                  key: "close_from",
                  applied:
                    search.close_from === undefined
                      ? undefined
                      : chipLabel(t("sales.pipeline.list.filterCloseFrom"), search.close_from),
                  onClear: () => {
                    views.write({ ...search, close_from: undefined });
                  },
                  control: (
                    <Field label={t("sales.pipeline.list.filterCloseFrom")}>
                      <input
                        type="month"
                        value={search.close_from ?? ""}
                        data-testid="sales-deals-close-from"
                        onChange={(event) => {
                          views.write({
                            ...search,
                            close_from:
                              event.target.value.length > 0 ? event.target.value : undefined,
                          });
                        }}
                      />
                    </Field>
                  ),
                },
                {
                  key: "close_to",
                  applied:
                    search.close_to === undefined
                      ? undefined
                      : chipLabel(t("sales.pipeline.list.filterCloseTo"), search.close_to),
                  onClear: () => {
                    views.write({ ...search, close_to: undefined });
                  },
                  control: (
                    <Field label={t("sales.pipeline.list.filterCloseTo")}>
                      <input
                        type="month"
                        value={search.close_to ?? ""}
                        data-testid="sales-deals-close-to"
                        onChange={(event) => {
                          views.write({
                            ...search,
                            close_to:
                              event.target.value.length > 0 ? event.target.value : undefined,
                          });
                        }}
                      />
                    </Field>
                  ),
                },
                {
                  key: "lost_reason",
                  applied:
                    search.lost_reason === undefined
                      ? undefined
                      : chipLabel(
                          t("sales.pipeline.list.filterLostReason"),
                          lostReasonLabel(t, search.lost_reason),
                        ),
                  onClear: () => {
                    views.write({ ...search, lost_reason: undefined });
                  },
                  control: (
                    <Field label={t("sales.pipeline.list.filterLostReason")}>
                      <Select
                        value={search.lost_reason ?? ""}
                        data-testid="sales-deals-lost-reason"
                        onChange={(event) => {
                          const value = event.target.value;
                          views.write({
                            ...search,
                            lost_reason: LOST_REASON_CODES.find((code) => code === value),
                          });
                        }}
                      >
                        <option value="">{t("sales.pipeline.list.anyLostReason")}</option>
                        {LOST_REASON_CODES.map((code) => (
                          <option key={code} value={code}>
                            {lostReasonLabel(t, code)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ),
                },
                {
                  key: "rotting",
                  applied:
                    search.rotting === true ? t("sales.pipeline.list.filterRotting") : undefined,
                  onClear: () => {
                    views.write({ ...search, rotting: undefined });
                  },
                  control: (
                    <CheckboxField
                      label={t("sales.pipeline.list.filterRotting")}
                      checked={search.rotting === true}
                      data-testid="sales-deals-rotting"
                      onChange={(checked) => {
                        views.write({ ...search, rotting: checked ? true : undefined });
                      }}
                    />
                  ),
                },
                {
                  key: "needs_next_step",
                  applied:
                    search.needs_next_step === true
                      ? t("sales.pipeline.list.filterNeedsNextStep")
                      : undefined,
                  onClear: () => {
                    views.write({ ...search, needs_next_step: undefined });
                  },
                  control: (
                    <CheckboxField
                      label={t("sales.pipeline.list.filterNeedsNextStep")}
                      checked={search.needs_next_step === true}
                      data-testid="sales-deals-needs-next-step"
                      onChange={(checked) => {
                        views.write({ ...search, needs_next_step: checked ? true : undefined });
                      }}
                    />
                  ),
                },
              ]}
            />
          </>
        )}

        {!refused ? <ViewSwitcher views={views} /> : null}

        {refused || listRefused ? (
          <ForbiddenState title={t("sales.access.title")} body={t("sales.access.pipeline")} />
        ) : null}

        {!refused && paused && rows.length === 0 ? (
          <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
        ) : null}

        {!refused && page.isPending && !paused && rows.length === 0 ? (
          <TableSkeleton rows={5} cols={columns.length} label={t("sales.pipeline.list.loading")} />
        ) : null}

        {!refused &&
        !listRefused &&
        page.error !== null &&
        !(page.error instanceof UnauthorizedError) &&
        !valueOrderRefused ? (
          <ErrorState
            title={t("sales.pipeline.list.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(page.error)}
            onRetry={() => {
              void page.refetch();
            }}
          />
        ) : null}

        {!refused && !page.isPending && !paused && page.error === null && rows.length === 0 ? (
          isFiltered ? (
            <div data-testid="sales-deals-filtered-empty">
              <EmptyState
                title={t("sales.pipeline.list.filteredEmptyTitle")}
                body={t("sales.pipeline.list.filteredEmptyBody")}
                ctaLabel={t("sales.pipeline.clearFilters")}
                onCta={() => {
                  views.write({
                    ...search,
                    status: undefined,
                    stage: undefined,
                    owner: undefined,
                    customer: undefined,
                    close_from: undefined,
                    close_to: undefined,
                    rotting: undefined,
                    needs_next_step: undefined,
                    lost_reason: undefined,
                    q: undefined,
                  });
                }}
              />
            </div>
          ) : (
            <div data-testid="sales-deals-empty">
              <EmptyState
                title={t("sales.pipeline.list.emptyTitle")}
                body={t("sales.pipeline.list.emptyBody")}
                {...(canWrite
                  ? {
                      ctaLabel: t("sales.pipeline.newDeal"),
                      onCta: () => {
                        setCreating(true);
                      },
                    }
                  : {})}
              />
            </div>
          )
        ) : null}

        {rows.length > 0 ? (
          <>
            <DataTable
              pager={bookPager(t, i18n.language, pageView, {
                turning,
                onNext: () => {
                  const next = shownData?.next_cursor;
                  if (next === null || next === undefined) return;
                  pages.next(next, rows.length);
                },
                onPrev: () => {
                  pages.prev();
                },
              })}
              data-testid="sales-deals-table"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              empty={null}
              caption={t("sales.pipeline.list.caption")}
              rowTestId="sales-deal-row"
              rowProps={(row) => ({
                "data-deal-id": row.id,
                "data-status": row.status,
                "data-stage-id": row.stage_id,
                "data-rotting": row.rotting ? "true" : "false",
              })}
              sticky
              keyboardNav
              // WRAP BEFORE WIDEN (design-system §3.3), as on the customer
              // book: ten columns in Russian («Месяц закрытия», «Звонок ·
              // Просрочено с 29 июн. 2026 г.») held the list 1458 px wide at
              // a 1440 px viewport. With `fit` a header label and a cell's
              // chip take a second line, and the table stays inside the glass.
              fit
              columnConfig={views.columnConfig}
              sort={{
                value: order,
                onChange: setOrder,
                toggleLabel: (header: string, next: "ascending" | "descending") =>
                  next === "descending"
                    ? t("sales.pipeline.list.sortDesc", { column: header })
                    : t("sales.pipeline.list.sortAsc", { column: header }),
              }}
              peek={{
                key: (row) => peekCode("opportunity", row.id),
                current: peek.code,
                onOpen: (code) => {
                  peek.open(code);
                },
                onClose: peek.close,
                label: t("sales.peek.open"),
                rowLabel: (row) => t("sales.peek.openNamed", { name: row.title }),
              }}
              onRowClick={(row) => {
                void navigate({
                  to: "/sales/opportunities/$opportunityId",
                  params: { opportunityId: row.id },
                });
              }}
            />
          </>
        ) : null}
      </ViewsRegion>

      {closing?.outcome === WON_CHOICE ? (
        <WinDealModal
          title={closing.deal.title}
          dealId={closing.deal.id}
          customerId={closing.deal.customer_id}
          pending={win.isPending}
          error={win.error}
          onClose={closeDialog}
          onConfirm={(body) => {
            win.mutate({ id: closing.deal.id, body }, { onSuccess: closeDialog });
          }}
        />
      ) : null}
      {closing?.outcome === LOST_CHOICE ? (
        <LoseDealModal
          dealId={closing.deal.id}
          title={closing.deal.title}
          pending={lose.isPending}
          error={lose.error}
          onClose={closeDialog}
          onConfirm={(choice) => {
            lose.mutate(
              {
                id: closing.deal.id,
                body: {
                  reason_code: choice.reason,
                  ...(choice.note !== undefined ? { note: choice.note } : {}),
                },
              },
              { onSuccess: closeDialog },
            );
          }}
        />
      ) : null}

      {creating ? (
        <NewDealModal
          lockedCustomerId={undefined}
          stages={stageList}
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}

      <SalesPeek peek={peek} />
    </section>
  );
}
